import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Camera, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Weighbridge Calibration form.
 *
 * Formula (per SOP): coils 1..N are placed on the weighbridge sequentially. For each coil,
 * the operator enters the coil's tag weight AND the cumulative gross reading on the
 * weighbridge after adding that coil. The individual weighbridge reading for each coil
 * is auto-calculated as Gross(N) - Gross(N-1) (for coil 1, this equals Gross(1)).
 *
 * Totals:
 *   Total Tag Weight        = sum of all tag weights
 *   Final Cumulative Gross  = last coil's cumulative gross reading
 *   Variation               = Final Cumulative Gross - Total Tag Weight
 *   Variation %             = (Variation / Total Tag Weight) × 100
 */
export default function CalibrationForm({ form, setField }) {
  const coils = form.coils || [{ coil_no: "", tag_weight: "", gross_cumulative: "", tag_photo_url: "", slip_photo_url: "" }];
  const [uploading, setUploading] = useState({});

  const setCoils = (updated) => setField("coils", updated);

  const addCoil = () => {
    setCoils([...coils, { _uid: crypto.randomUUID(), coil_no: "", tag_weight: "", gross_cumulative: "", tag_photo_url: "", slip_photo_url: "" }]);
  };

  const removeCoil = (idx) => {
    if (coils.length <= 1) return;
    setCoils(coils.filter((_, i) => i !== idx));
  };

  const updateCoil = (idx, key, val) => {
    setCoils(coils.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  };

  // Individual weighbridge weight = Gross(N) - Gross(N-1)
  const calcIndividual = (idx) => {
    const curr = parseFloat(coils[idx]?.gross_cumulative) || 0;
    const prev = idx === 0 ? 0 : (parseFloat(coils[idx - 1]?.gross_cumulative) || 0);
    return (curr - prev).toFixed(2);
  };

  const totalTag = coils.reduce((s, c) => s + (parseFloat(c.tag_weight) || 0), 0).toFixed(2);
  const finalCumulative = (parseFloat(coils[coils.length - 1]?.gross_cumulative) || 0).toFixed(2);
  const totalVar = (parseFloat(finalCumulative) - parseFloat(totalTag)).toFixed(2);
  const totalVarPct = parseFloat(totalTag) > 0 ? (((parseFloat(finalCumulative) - parseFloat(totalTag)) / parseFloat(totalTag)) * 100).toFixed(2) : "0.00";

  const photoButtonLabel = (coil, idx, kind) => {
    const isUploading = !!uploading[`${idx}_${kind}`];
    const url = kind === "tag" ? coil.tag_photo_url : coil.slip_photo_url;
    if (isUploading) return "Uploading...";
    if (url) return kind === "tag" ? "Tag Photo OK" : "Slip Photo OK";
    return kind === "tag" ? "Tag Photo" : "Slip Photo";
  };

  const handlePhotoCapture = async (idx, photoType) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading((prev) => ({ ...prev, [`${idx}_${photoType}`]: true }));
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          const { data } = await api.post("/upload/photo", { photo: reader.result });
          updateCoil(idx, photoType === "tag" ? "tag_photo_url" : "slip_photo_url", data.url);
          toast.success(`${photoType === "tag" ? "Tag" : "Slip"} photo uploaded`);
          setUploading((prev) => ({ ...prev, [`${idx}_${photoType}`]: false }));
        };
        reader.readAsDataURL(file);
      } catch {
        toast.error("Photo upload failed");
        setUploading((prev) => ({ ...prev, [`${idx}_${photoType}`]: false }));
      }
    };
    input.click();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Calibration Date</Label>
          <Input type="date" value={form.calibration_date || ""} onChange={(e) => setField("calibration_date", e.target.value)} className="h-11" data-testid="field-cal-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
          <Input value={form.remarks || ""} onChange={(e) => setField("remarks", e.target.value)} className="h-11" data-testid="field-cal-remarks" />
        </div>
      </div>

      {/* Formula explainer */}
      <div className="text-[11px] text-gray-500 bg-blue-50/60 border border-blue-100 rounded p-2 leading-relaxed" data-testid="cal-formula-help">
        Place coils one-by-one. For each coil, enter its <strong>Tag Weight</strong> and the
        <strong> Cumulative Gross Weight</strong> on the bridge after it is added. The individual
        weighbridge reading for each coil is auto-calculated. Final % variance compares the last
        cumulative gross to the sum of all tag weights.
      </div>

      <div className="border rounded-lg p-3 bg-slate-50/50 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">
            Wire Rod Coils ({coils.length})
          </Label>
          <Button type="button" size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={addCoil} data-testid="add-coil-btn">
            <Plus size={12} /> Add Coil
          </Button>
        </div>

        {coils.map((coil, idx) => (
          <div key={coil._uid || `coil-${idx}`} className="bg-white p-3 rounded border space-y-2" data-testid={`coil-row-${idx}`}>
            <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end">
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500">Coil No.</Label>
                <Input value={coil.coil_no} onChange={(e) => updateCoil(idx, "coil_no", e.target.value)} className="h-9 text-sm" placeholder="#" data-testid={`coil-no-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500">Tag Weight (Kg)</Label>
                <Input type="number" step="0.01" value={coil.tag_weight} onChange={(e) => updateCoil(idx, "tag_weight", e.target.value)} className="h-9 text-sm" placeholder="0.00" data-testid={`tag-wt-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500">Cumulative Gross (Kg)</Label>
                <Input type="number" step="0.01" value={coil.gross_cumulative} onChange={(e) => updateCoil(idx, "gross_cumulative", e.target.value)} className="h-9 text-sm" placeholder="0.00" data-testid={`gross-cumulative-${idx}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-500">Indiv. Weighbridge</Label>
                <div className="h-9 flex items-center px-2 border rounded text-sm font-semibold bg-emerald-50 border-emerald-200 text-emerald-700" data-testid={`weighbridge-indiv-${idx}`}>
                  {calcIndividual(idx)} Kg
                </div>
              </div>
              <div>
                <Button type="button" size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-400 hover:text-red-600" onClick={() => removeCoil(idx)} disabled={coils.length <= 1} data-testid={`remove-coil-${idx}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Button type="button" size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handlePhotoCapture(idx, "tag")} disabled={!!uploading[`${idx}_tag`]} data-testid={`photo-tag-${idx}`}>
                <Camera size={11} /> {photoButtonLabel(coil, idx, "tag")}
              </Button>
              {coil.tag_photo_url && (
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => updateCoil(idx, "tag_photo_url", "")}>
                  <X size={12} />
                </Button>
              )}
              <Button type="button" size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handlePhotoCapture(idx, "slip")} disabled={!!uploading[`${idx}_slip`]} data-testid={`photo-slip-${idx}`}>
                <Camera size={11} /> {photoButtonLabel(coil, idx, "slip")}
              </Button>
              {coil.slip_photo_url && (
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400" onClick={() => updateCoil(idx, "slip_photo_url", "")}>
                  <X size={12} />
                </Button>
              )}
            </div>
          </div>
        ))}

        <div className="bg-[#1B3C87]/5 rounded-lg p-3 mt-2">
          <Label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2 block">Cumulative Summary</Label>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Total Tag Wt</p>
              <p className="text-sm font-bold" data-testid="total-tag-weight">{totalTag} Kg</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Final Cumulative Gross</p>
              <p className="text-sm font-bold" data-testid="final-cumulative-gross">{finalCumulative} Kg</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Variation</p>
              <p className={`text-sm font-bold ${parseFloat(totalVar) === 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="total-variation">{totalVar} Kg</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-400 uppercase">Variation %</p>
              <p className={`text-sm font-bold ${parseFloat(totalVarPct) === 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid="total-variation-pct">{totalVarPct}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
